<template>
  <main class="a-good-company">
    <AGoodCompanyStage>
      <template #content>
        <CdnImage
          class="a-good-company__logo"
          render-without-figure
          :src="stage.logo"
          :alt="stage.title"
        />
        <Heading
          tag="h1"
          size="lg"
          font-weight="ultra"
          text-uppercase
          spaces-bottom
          class="a-good-company__subtitle"
        >
          {{ stage.subtitle }}
        </Heading>
      </template>
    </AGoodCompanyStage>

    <GridContainer
      class="a-good-company__container--spaces"
      padding
    >
      <Heading
        tag="h2"
        size="lg"
        font-weight="ultra"
        text-uppercase
        spaces-bottom
      >
        {{ intro.title }}
      </Heading>
      <div class="a-good-company__intro-text-container">
        <Paragraph align="left">
          {{ intro.text }}
        </Paragraph>
        <CdnImage
          class="a-good-company__intro-image"
          render-without-figure
          :src="intro.image"
          :alt="stage.hero.alt"
          lazy
        />
      </div>
    </GridContainer>
    <GridContainer
      class="a-good-company__container--spaces"
      padding
    >
      <AGoodCompanyFeatures />
    </GridContainer>
    <div class="a-good-company__logo--video">
      <CdnImage
        :src="stage.videoLogo"
        :alt="stage.title"
        render-without-figure
        lazy
      />
    </div>
    <ProductOfferTileXlContainer
      v-if="!!productOfferTileXl"
      class="a-good-company__container--spaces-top"
      :offer="productOfferTileXl.offer"
      :title="productOfferTileXl.title"
      :description="productOfferTileXl.copy"
      :video="productOfferTileXl.video"
      :product-image="productOfferTileXl.productImage"
      product-image-aspect-ratio="1"
      :video-aria-label="`Informationsvideo für ${productOfferTileXl.title}`"
      video-button-position="right"
      show-local-image
      product-image-sizes="xs:50vw lg:500px"
    />
    <ProductOfferTileSMContainer
      :items="getOffersByGroup(offers, 'mTopTeaser', 2)"
      tile-type="medium"
    />
    <GridContainer>
      <GridRow>
        <GridColumn
          v-for="item in uspItems"
          :key="item.id"
          :sm="6"
          :md="4"
          class="a-good-company__usp-col"
        >
          <SvgIcon
            :image="item.icon"
            class="a-good-company__usp-icon"
          />
          <Heading
            class="a-good-company__usp-title"
            size="md"
            tag="h3"
            align="center"
          >
            {{ item.title }}
          </Heading>
          <Paragraph
            class="a-good-company__usp-copy"
            align="center"
          >
            {{ item.copy }}
          </Paragraph>
        </GridColumn>
      </GridRow>
    </GridContainer>
    <ProductOfferTileSMContainer
      class="a-good-company__container--spaces"
      :items="getOffersByGroup(offers, 'sTiles', 3)"
    />
    <ProductOfferTileSMContainer
      :items="getOffersByGroup(offers, 'mTeaser', 4)"
      tile-type="medium"
    />
    <GridContainer
      class="a-good-company__container--spaces"
      padding
    >
      <ImageTeaser
        :heading="{
          text: banner.title,
          tag: 'h2',
        }"
        tile-size="m"
        animate-tile
        position-y-desktop="center"
        :animation-delay="1"
        position-y="center"
        lazy
        class="a-good-company__banner"
        :background="banner.background"
      >
        <template #button>
          <LinkButton
            :to="banner.button.url"
            primary
            tag="nuxt-link"
          >
            {{ banner.button.label }}
          </LinkButton>
        </template>
      </ImageTeaser>
    </GridContainer>
  </main>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useLazyAsyncData } from '#imports';
import {
  Heading,
  Paragraph,
  GridContainer,
  GridRow,
  GridColumn,
  LinkButton,
  ImageTeaser,
} from '@i22/td-longtail-vue-components';
import AGoodCompanyStage from '~/components/a-good-company-stage.vue';
import AGoodCompanyFeatures from '~/components/a-good-company-features.vue';
import CdnImage from '~/components/cdn-image.vue';
import ProductOfferTileXlContainer from '~/components/product-offer-tile-xl-container.vue';
import ProductOfferTileSMContainer from '~/components/product-offer-tile-s-m-container.vue';

// ICONS
import AwardOutline from '~/assets/images/a-good-company/award_outline.svg';
import CarbonDioxideOutline from '~/assets/images/a-good-company/carbon-dioxide_outline.svg';
import PlantOutline from '~/assets/images/a-good-company/plant_outline.svg';
import RegionalOutline from '~/assets/images/a-good-company/regional_outline.svg';
import ShoppingBagOutline from '~/assets/images/a-good-company/shopping-bag_outline.svg';
import SynchronizeGraphical from '~/assets/images/a-good-company/synchronize_graphical.svg';
import {
  getOffersByGroup,
  loadOffersForPromotions,
  type Promotion,
  type PromotionOffer,
} from '~/lib/offers-by-group';
import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';

const PRODUCT_TEASER_DATA: Promotion[] = [
  {
    articleNumber: '99933970',
    title: 'agood Case Telekom T Phone Pro',
    copy: '100 % kompostierbar',
    bgColor: 'beige',
    tileSize: 's',
    showLocalTitle: false,
    tileGroups: ['sTiles'],
  },
  {
    articleNumber: '99933852',
    title: 'agood Cover No Plastic Apple iPhone 14 Plus',
    copy: 'Schutz vor Stößen und Kratzern',
    bgColor: 'beige',
    tileSize: 's',
    showLocalTitle: false,
    tileGroups: ['sTiles'],
  },
  {
    articleNumber: '99934627',
    title: 'MagSafe  Ring',
    copy: 'Austauschbar für mehr Nachhaltigkeit',
    bgColor: 'beige',
    tileSize: 's',
    showLocalTitle: false,
    tileGroups: ['sTiles'],
  },
  {
    articleNumber: '99932402',
    title: 'agood Case Apple iPhone 12 mini',
    copy: 'Hergestellt aus Flachs und Hanf',
    bgColor: 'beige',
    tileSize: 's',
    showLocalTitle: false,
    tileGroups: ['sTiles'],
  },
  {
    articleNumber: '99932552',
    title: 'agood Case Apple iPhone 13 Mini',
    copy: 'Schutz vor Stößen und Kratzern',
    bgColor: 'beige',
    tileSize: 's',
    showLocalTitle: false,
    tileGroups: ['sTiles'],
  },
  {
    articleNumber: '99934626',
    title: 'agood PLNTPRTCT JTGF Case MagSafe Apple iPhone 15 Pro Max',
    copy: '100% plastikfrei',
    bgColor: 'beige',
    tileSize: 's',
    showLocalTitle: false,
    tileGroups: ['sTiles'],
  },
  {
    articleNumber: '99934619',
    title: 'agood CLRPRTCT Case MagSafe Apple iPhone 15',
    copy: '100% Upcycled aus Trinkgläsern und ausrangierten Süßigkeitendosen',
    ambientImage: {
      src: '/content/content-images/landingpages/a-good-company/7c13fcaf54b2f520c04b47b07674ef73/kachel-produktangebot-agood.jpg',
      alt: 'Zu 100% aus Pflanzen hergestelltes Case',
    },
    video: {
      src: '/content/content-videos/landingpages/a-good-company/d6587375ab48b73e5ae702843e89b481/agood-case.mp4',
    },
    productImage: {
      src: '/content/content-images/landingpages/themenwelt-refresh/2be2e0ae355a530faf905519456dd9f4/hero-product-1000x1000.png',
      alt: 'Zu 100% aus Pflanzen hergestelltes Case',
      posY: {
        xs: '-15%',
        sm: '-15%',
        md: '5%',
        lg: '5%',
        xl: '5%',
      },
    },
    tileGroups: ['productOfferTileXl'],
  },
  {
    articleNumber: '99934619',
    title: 'agood CLRPRTCT Case MagSafe Apple iPhone 15',
    copy: '100% Upcycled aus Trinkgläsern und ausrangierten Süßigkeitendosen',
    bgColor: 'light-grey',
    tileSize: 'm',
    showLocalTitle: false,
    tileGroups: ['mTopTeaser'],
  },
  {
    articleNumber: '99934625',
    title: 'agood PLNTPRTCT JTGF Case MagSafe Apple iPhone 15 Pro',
    copy: '100% plastikfrei',
    bgColor: 'light-grey',
    tileSize: 'm',
    showLocalTitle: false,
    tileGroups: ['mTopTeaser'],
  },
  {
    articleNumber: '99934615',
    title: 'agood PLNTPRTCT Case MagSafe Apple iPhone 15',
    copy: 'Verbesserter Schutz der Kamera.',
    bgColor: 'beige',
    tileSize: 'm',
    showLocalTitle: false,
    tileGroups: ['mTeaser'],
  },
  {
    articleNumber: '99933969',
    title: 'agood Case Telekom T Phone',
    copy: '100 % kompostierbar',
    bgColor: 'beige',
    tileSize: 'm',
    showLocalTitle: false,
    tileGroups: ['mTeaser'],
  },
  {
    articleNumber: '99932410',
    title: 'agood Case Apple iPhone 6 / 7 / 8',
    copy: 'Hergestellt aus Flachs und Hanf',
    bgColor: 'beige',
    tileSize: 'm',
    showLocalTitle: false,
    tileGroups: ['mTeaser'],
  },
  {
    articleNumber: '99933970',
    title: 'agood Case Telekom T Phone Pro',
    copy: '100 % kompostierbar',
    bgColor: 'beige',
    tileSize: 'm',
    showLocalTitle: false,
    tileGroups: ['mTeaser'],
  },
];
Track({ page_type: 'theme.landing' });

getHeader({
  title: ref('agood company: Handy Cases'),
  description: ref(
    'Schutz für das Smartphone & gut für die Umwelt, da nachhaltig produziert und komplett kompostierbar. Jetzt die Handy Cases von agood Company entdecken!'
  ),
});

const { data: offers, error: errorData } = useLazyAsyncData(
  'AGoodCompany',
  (): Promise<PromotionOffer[]> => loadOffersForPromotions(PRODUCT_TEASER_DATA, true)
);

const stage = {
  hero: {
    src: '/content/content-images/landingpages/a-good-company/511ac1d5ccb1a749a97a42c95843cf22/buehne-produktimage-v2-agood.png',
    alt: 'agood Company',
  },
  greenMagenta: {
    src: '/content/content-images/landingpages/a-good-company/green-magenta-label/14d4e7c8de2128bd556d50494eeb1889/buehne-produktimage-v2-agood-mit-green-magenta.png',
    alt: 'agood Company',
  },
  logo: '/content/content-images/landingpages/themenwelt-refresh/71432a5ca2cedd08035e20a882ad9cc4/agood-logo-horizontal.svg',
  videoLogo:
    '/content/content-images/landingpages/themenwelt-refresh/ff0eb4829916e2f2481f2e49200a5793/logo_agood-video.svg',

  title: 'agood Company',
  subtitle: 'Schütze Dein neues iPhone 15   mit alten Süßigkeitendosen',
};

const intro = {
  title: 'agood – Nachhaltigkeit an erster Stelle',
  text: 'agood Company hat sich Nachhaltigkeit zur Aufgabe gemacht. Ihre Produkte wie z.B. die Handy Cases stellt das privat geführte Unternehmen aus den Resten des biologischen Leinsamenanbaus her und setzt dabei für seine selbstkreierten Designs auf einen klimaneutralen Produktionsprozess.',
  image:
    '/content/content-images/landingpages/themenwelt-refresh/c7697bc47fc45ffa346f7d0db6efb0e0/green-magenta.svg',
};
const uspItems = [
  {
    title: 'Kreislaufwirtschaft',
    icon: SynchronizeGraphical,
    copy: 'Aus alten Cases entstehen neue: Das Recycling erfolgt in einer kontrollierten Umgebung mit lokalen deutschen Partnern.',
  },
  {
    title: 'Materialien',
    icon: PlantOutline,
    copy: 'Die Rohmaterialien kommen aus Schweden; auch die Forschung an weiteren nachhaltigen Materialien ist dort in vollem Gange.',
  },
  {
    title: 'Lokale Produktion: ',
    icon: RegionalOutline,
    copy: 'Die Leinsamen werden in Schweden angepflanzt, geerntet und das Rohmaterial von lokalen Unternehmen weiterverarbeitet.',
  },
  {
    title: 'B-Corp-Zertifizierung',
    icon: AwardOutline,
    copy: 'agood Company ist Teil des B-Corp-Movements und hat sich damit selbst verpflichtet, hohe Standards in ökologischer und sozialer Nachhaltigkeit einzuhalten.',
  },
  {
    title: 'CO2-negativ',
    icon: CarbonDioxideOutline,
    copy: 'agood Company arbeitet klimaneutral und erreicht seit der Gründung im Jahr 2019 einen negativen CO2-Footprint.',
  },
  {
    title: 'Verpackung',
    icon: ShoppingBagOutline,
    copy: 'Die Verpackung besteht aus recyceltem Papier und ist zu 100 % plastikfrei. Auch der Versand erfolgt so umweltschonend wie möglich.',
  },
];
const banner = {
  background: {
    src: '/content/content-images/landingpages/themenwelt-refresh/000d3361283f66af75581f1537dcaded/teaser-weitere-artikel-s@2x.jpg',
    srcSm:
      '/content/content-images/landingpages/themenwelt-refresh/e34d5b60372f9e23c939455eb24135d3/teaser-weitere-artikel-m.jpg',
    srcLg:
      '/content/content-images/landingpages/themenwelt-refresh/e94d4f53f8f7ccb7347a883a9837f329/teaser-weitere-artikel@2x.jpg',
    sizes: 'xs:100vw sm:100vw lg:100vw',
    style: 'aspect-ratio: 732 / 269',
  },
  title: 'Entdecke weitere AGood Company Produkte',
  button: {
    url: '/zubehoer/_agood',
    label: 'Alle agood Company Produkte',
  },
};

const productOfferTileXl = computed(
  () => getOffersByGroup(offers.value, 'productOfferTileXl', 1)?.[0]
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

$image-text-grid-row: '.image-text-grid-row';
$product-offer-tile-xl: '.product-offer-tile-xl';

.a-good-company {
  &__container {
    &--spaces {
      padding-top: $grid-base * 2;
      padding-bottom: $grid-base * 2;
      @include mq($mq-sm) {
        padding-top: $grid-base * 4;
        padding-bottom: $grid-base * 4;
      }
    }
    &--spaces-top {
      padding-top: $grid-base * 2;
      @include mq($mq-sm) {
        padding-top: $grid-base * 4;
      }
    }
  }
  &__logo {
    width: 320px;
    aspect-ratio: 7 / 1;
    @include mq($mq-md) {
      width: 440px;
    }
    &--video {
      display: flex;
      justify-content: center;

      img {
        width: 292px;
        aspect-ratio: 146 / 55;
      }
    }
  }

  &__subtitle {
    max-width: fit-content;
  }
  &__intro {
    &-text-container {
      display: flex;
      flex-direction: column;
      @include mq($mq-md) {
        flex-direction: row;
      }
    }
    &-image {
      width: 60%;
      align-self: center;
      aspect-ratio: 97 / 40;
      @include mq($mq-md) {
        max-width: 35%;
      }
    }
  }
  &__hero {
    max-width: 100%;
  }
  &__hero-updated {
    max-width: 100%;
  }
  &__title {
    margin-bottom: 0.2em;
  }
  &__usp-col {
    text-align: center;
    padding: $grid-base * 3 0;
    @include mq($mq-sm) {
      padding: $grid-base * 6 0;
    }
  }
  &__usp-icon {
    height: 55px;
    width: auto;
    @include mq($mq-sm) {
      height: 70px;
    }
  }
  &__usp-copy {
    max-width: 360px;
    margin: 0 auto;
  }
  &__banner {
    padding-bottom: $grid-base * 6;
    padding-top: $grid-base * 6;
    @include mq($mq-md) {
      min-height: 520px;
    }
    :deep(.image-teaser__title) {
      max-width: 430px;
    }

    &-image {
      :deep(img) {
        width: 100%;
        aspect-ratio: 732 / 269;
      }
    }
  }

  :deep(#{$image-text-grid-row}) {
    max-width: 1464px;
    padding: $grid-base;
    margin: $grid-base * 3 auto 0 auto;
    img {
      height: auto;
      width: 100%;
      max-width: 100%;
      object-fit: contain;
    }
  }
  :deep(#{$image-text-grid-row}__column-image) {
    min-height: initial;
  }
  :deep(#{$image-text-grid-row}__copy) {
    margin-bottom: 0;
  }
  :deep(#{$image-text-grid-row}__heading) {
    @include font-size(24px, 30px);
    @include mq($mq-sm) {
      @include font-size(36px, 42px);
    }
  }

  :deep(#{$product-offer-tile-xl}__product-image) {
    max-width: 58%;
    transform: translateY(0%);
    @include mq($mq-sm) {
      transform: translateY(0%);
    }
    @include mq($mq-md) {
      max-width: 100%;
      transform: translateY(5%);
    }
  }

  :deep(.product-offer-detail-link__link) {
    img {
      aspect-ratio: 1 / 1;
      width: 100%;
    }
  }
}

.green-magenta-teaser {
  &__container {
    margin: $grid-base * 4 0;
    background-color: var(--color-primary);
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    background-size: contain;
    &::after,
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      background-repeat: no-repeat;
      background-size: contain;
    }
    &::after {
      top: 100%;
      height: 50px;
      background-size: 55%;
      background-position: left top;
      background-image: url('/content/content-images/landingpages/a-good-company/green-magenta-label/d8a239d03d55be4e47e4493b529b9240/green-magenta-label-dreieck-unten-links.svg');
    }
    &::before {
      height: 60px;
      background-size: auto 100%;
      background-position: left bottom;
      transform: translateY(-100%);
      background-image: url('/content/content-images/landingpages/a-good-company/green-magenta-label/9183f2f6c20f1764afc67a8f605267f2/green-magenta-box-dreiecke-oben-mobil.svg');
    }

    @include mq($mq-sm) {
      &::after {
        background-size: 40%;
      }
      &::before {
        height: 70px;
        background-position: bottom left;
        background-size: cover;
        background-image: url('/content/content-images/landingpages/a-good-company/green-magenta-label/6a97d0f41f1f1d8c48123a77b8307aee/green-magenta-box-dreiecke-oben-desktop.svg');
      }
    }

    @include mq($mq-md) {
      flex-direction: row;
      &::after {
        background-size: 30%;
      }
      &::before {
        height: 70px;
        top: 0;
        background-size: contain;
      }
    }
    @include mq($mq-lg) {
      &::before {
        height: 100px;
      }
    }
  }

  &__right-asset {
    width: 95%;
    height: 45px;
    bottom: 0;
    left: 0;
    position: absolute;
    background-repeat: no-repeat;
    background-position: right center;
    background-size: contain;
    background-image: url('/content/content-images/landingpages/a-good-company/green-magenta-label/cc16383cd2b2145c3f1a2cd163cc22a2/green-magenta-label-dreieck-unten-rechts.svg');
    transform: translateY(100%);

    @include mq($mq-sm) {
      height: 60px;
    }
  }

  &__content {
    border-top: solid 3px var(--color-secondary);
    padding: $grid-base * 2 $grid-base $grid-base;
    margin: $grid-base;

    @include mq($mq-md) {
      padding: $grid-base 0 $grid-base * 2 $grid-base;
      border-top: none;
      border-left: solid 3px var(--color-secondary);
    }
  }

  &__title {
    color: var(--color-secondary);
    text-align: center;
    @include font-size(30px, 40px);
    @include mq($mq-sm) {
      @include font-size(45px, 50px);
      text-align: left;
    }
  }
}
</style>
